import { generatePath } from 'react-router-dom';

import { WORKLOAD_CATEGORIES, WORKLOAD_VIEWS } from './_constants';
import type { WorkloadCategory, WorkloadView } from './_constants';

// Mirrors apps/console-oneclick/src/constants/navigation.ts
export const INVENTORY_WORKLOAD_BASE_PATH =
  '/automation/external-clusters/:clusterId/inventory/workloads';

type InventoryWorkloadsPathArgs = {
  clusterId: string;
};

type InventoryWorkloadsViewPathArgs = InventoryWorkloadsPathArgs & {
  view: WorkloadView;
  category?: WorkloadCategory;
};

type InventoryWorkloadPathArgs = InventoryWorkloadsPathArgs & {
  workloadId: string;
};

type InventoryWorkloadViewPathArgs = InventoryWorkloadPathArgs & {
  view: WorkloadView;
  category?: WorkloadCategory;
};

const viewsWithoutCategory: WorkloadView[] = [
  WORKLOAD_VIEWS.META,
  WORKLOAD_VIEWS.EVENTS,
  WORKLOAD_VIEWS.VERTICAL_AUTOSCALING,
  WORKLOAD_VIEWS.HORIZONTAL_AUTOSCALING,
];

const buildCategorySegment = (
  view: WorkloadView,
  category?: WorkloadCategory
): string => {
  // Meta, Events, and rightsizing settings have no category segment.
  if (viewsWithoutCategory.includes(view)) {
    return '';
  }

  if (!category || category === WORKLOAD_CATEGORIES.DEFAULT) {
    // List view has no default category route; fall back to the list index.
    return view === WORKLOAD_VIEWS.LIST
      ? ''
      : `/${WORKLOAD_CATEGORIES.DEFAULT}`;
  }

  return `/${category}`;
};

export const buildInventoryWorkloadsViewPath = ({
  clusterId,
  view,
  category,
}: InventoryWorkloadsViewPathArgs) => {
  const base = generatePath(`${INVENTORY_WORKLOAD_BASE_PATH}/${view}`, {
    clusterId,
  });

  return `${base}${buildCategorySegment(view, category)}`;
};

export const buildInventoryWorkloadPath = ({
  clusterId,
  workloadId,
}: InventoryWorkloadPathArgs) =>
  generatePath(`${INVENTORY_WORKLOAD_BASE_PATH}/:workloadId`, {
    clusterId,
    workloadId,
  });

export const buildInventoryWorkloadViewPath = ({
  clusterId,
  workloadId,
  view,
  category,
}: InventoryWorkloadViewPathArgs) => {
  const base = generatePath(
    `${INVENTORY_WORKLOAD_BASE_PATH}/:workloadId/${view}`,
    { clusterId, workloadId }
  );

  return `${base}${buildCategorySegment(view, category)}`;
};

type InventoryWorkloadEventDetailsPathArgs = InventoryWorkloadPathArgs & {
  eventId: string;
  createdAt: string;
};

export const buildInventoryWorkloadEventDetailsPath = ({
  clusterId,
  workloadId,
  eventId,
  createdAt,
}: InventoryWorkloadEventDetailsPathArgs) => {
  const base = generatePath(
    `${INVENTORY_WORKLOAD_BASE_PATH}/:workloadId/events/:eventId`,
    { clusterId, workloadId, eventId }
  );

  const urlParams = new URLSearchParams();
  urlParams.set('createdAt', createdAt);

  return `${base}?${urlParams.toString()}`;
};
